import type { InventoryLocation, ItemInventory } from '../../../services/productionService';

/** What the Inventory cell and its sort show for the selected location. */
export interface ShownInventory {
  rows: InventoryLocation[];
  total: number;
}

/** The backend's figures for the selected location ('' = all); undefined when the item has no stock there. */
export const inventoryAt = (inventory: ItemInventory | undefined, location: string): ShownInventory | undefined => {
  if (!inventory) return undefined;
  if (!location) return inventory.locations.length ? { rows: inventory.locations, total: inventory.total_cases } : undefined;
  const row = inventory.locations.find(r => r.location_code === location);
  return row && { rows: [row], total: row.cases };
};

/**
 * Inventory Ratio: the TOTAL shown in the Inventory cell ÷ Prod Safe Qty, as a whole %. The page's only ratio math, used
 * for the cell, its sort and the live preview while typing. null when either is missing or Prod Safe Qty is 0.
 */
export const inventoryRatio = (total: number | undefined, safeQty: number | null | undefined): number | null =>
  total == null || !safeQty ? null : Math.round((total / safeQty) * 100);

/** A ratio as shown: 171%, or — for none. */
export const ratioText = (ratio: number | null): string => (ratio == null ? '—' : `${ratio}%`);

/** Prod Safe Qty as shown: 45 cs, or — when the item has none. */
export const safeQtyText = (quantity: number | null | undefined, cs: string): string =>
  quantity == null ? '—' : `${quantity} ${cs}`;

/** The edit box starts with the value; empty when the item has none. */
export const safeQtyDraftStart = (quantity: number | null | undefined): string =>
  quantity == null ? '' : String(quantity);

/** Blank saves as 0; null when not a whole number of 0 or more. */
export const parseSafeQtyDraft = (draft: string): number | null => {
  const quantity = draft.trim() === '' ? 0 : Number(draft);
  return Number.isSafeInteger(quantity) && quantity >= 0 ? quantity : null;
};

/** Distinct locations with stock as dropdown options, sorted by code. */
export const qsLocationOptions = (inventory: Record<string, ItemInventory>) => {
  const names = new Map(Object.values(inventory).flatMap(i => i.locations).map(l => [l.location_code, l.location_name]));
  return [...names.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([value, name]) => ({ value, label: name && name !== value ? `${value} - ${name}` : value }));
};
